'use strict';

/*
  BankAccount

  An object that represents a Bank Account resource. Data for this resource is stored through the
  Presenter/Model. Provides convenience methods for working with the resource.

  Parameters:
    presenter [APIPresenter] (required):
      Reference to the APIPresenter where the data for this bank account is stored.

    bankData [object] (required):
      Raw data fro each bank account from accounts model.
*/
function BankAccount(presenter, accountModel, errorHandler) {
  this.presenter = presenter;
  this.id = accountModel.id;
  this.ownershipVerified = accountModel.ownership_verified;
  this.balance = accountModel.balance;
  this.bankId = accountModel.bank_id;
  this.accountsArrayIndex = null;
  this.errorHandler = errorHandler;

  this._DEBUG = false;
  if (this._DEBUG) this.pageName = '[BankAccount]';

  this._getAccountArrayIndex = function () {
    if (this.accountsArrayIndex === null || this.presenter.getModelProperty('banking.accounts.bank_accounts[' + this.accountsArrayIndex + '].id') !== this.id) {
      var accounts = this.presenter.getModelProperty('banking.accounts.bank_accounts');
      for (var i = 0; i < accounts.length; i++) {
        if (accounts[i].id === this.id) {
          this.accountsArrayIndex = i;
          break;
        }
      }
    }
    return this.accountsArrayIndex;
  };

  // Initiate Plaid process for Selected bank (bank_accounts[${index}])
  this._initiatePlaid = function (successCallback, failureCallback) {
    var index = this._getAccountArrayIndex();
    if (this.presenter.hasAction('banking.accounts.bank_accounts[' + index + '].initiate_plaid')) {
      this.presenter.performAction('banking.accounts.bank_accounts[' + index + '].initiate_plaid', null, function (data, response) {
        if (response === 200) {
          if (typeof successCallback === 'function') {
            successCallback(data);
          }
        } else if (typeof failureCallback === 'function') {
          failureCallback(data, response);
        }
      });
    } else {
      failureCallback({ errors: [{ code: 404, key: 'no_action_error' }] }, 404);
    }
  };

  /*
    Parameters:
      options
        product_id [optional] - associates the bank account with an unique product id
  */
  this.associateWithProduct = function (product, successCallback, failureCallback, options) {
    var _this = this;

    if (this._DEBUG) console.log(this.pageName + ' - associateWithProduct() called');

    var payLoad = {};
    payLoad.product_type = product;

    if (options) {
      payLoad.product_id = options.product_id;
    }

    if (this.constructor.productType[product]) {
      var index = this._getAccountArrayIndex();
      if (this.presenter.hasAction('banking.accounts.bank_accounts[' + index + '].create_product_association')) {
        this.presenter.performAction('banking.accounts.bank_accounts[' + index + '].create_product_association', payLoad, function (data, response) {
          if (response === 200) {
            if (_this._DEBUG) console.log(_this.pageName + ' - associateWithProduct() - Success via - banking.accounts.bank_accounts[' + index + '].create_product_association');

            // The second 'this' of bind refers to the current BankAccount instance itself. It is passed to the success callback method as it first argument.
            _this.presenter.updateModel('banking.accounts.bank_accounts[' + index + ']', data, successCallback.bind(_this, _this, data, response));
          } else {
            if (_this._DEBUG) console.log(_this.pageName + ' - associateWithProduct() - Fail via - banking.accounts.bank_accounts[' + index + '].create_product_association');

            failureCallback(data, response);
          }
        });
      } else {
        failureCallback({ errors: [{ code: 404, key: 'no_action_error' }] }, 404);
      }
    } else {
      failureCallback({ errors: [{ code: 400, key: 'invalid_product_type' }] }, 400);
    }
  };

  this.disassociateWithProduct = function (product, successCallback, failureCallback) {
    var _this2 = this;

    if (this.constructor.productType[product]) {
      (function () {
        var index = _this2._getAccountArrayIndex();
        var associatedArray = _this2.presenter.getModelProperty('banking.accounts.bank_accounts[' + index + '].bank_account_product_associations');
        for (var i = 0; i < associatedArray.length; i++) {
          if (_this2.presenter.hasAction('banking.accounts.bank_accounts[' + index + '].bank_account_product_associations[' + i + '].delete_product_association') && _this2.presenter.getModelProperty('banking.accounts.bank_accounts[' + index + '].bank_account_product_associations[' + i + '].product_type') === product) {
            _this2.presenter.performAction('banking.accounts.bank_accounts[' + index + '].bank_account_product_associations[' + i + '].delete_product_association', null, function (data, response) {
              if (response === 200) {
                // The second 'this' of bind refers to the current BankAccount instance itself. It is passed to the success callback method as it first argument.
                _this2.presenter.updateModel('banking.accounts.bank_accounts[' + index + ']', data, successCallback.bind(_this2, _this2));
              } else {
                failureCallback(data, response);
              }
            });
            break;
          } else if (i === associatedArray.length - 1) {
            failureCallback({ errors: [{ code: 404, key: 'no_action_error' }] }, 404);
          }
        }
      })();
    } else {
      failureCallback({ errors: [{ code: 400, key: 'invalid_product_type' }] }, 400);
    }
  };

  this.getAssociatedProducts = function () {
    if (this._DEBUG) console.log(this.pageName + ' - getAssociatedProducts() called');

    var products = [];
    var index = this._getAccountArrayIndex();
    var associatedArray = this.presenter.getModelProperty('banking.accounts.bank_accounts[' + index + '].bank_account_product_associations');
    for (var i = 0; i < associatedArray.length; i++) {
      var association = associatedArray[i];
      if (products.indexOf(association.product_type) === -1) {
        products.push(association.product_type);
      }
    }
    return products;
  };

  this.verifyAccount = function (successCallback, failureCallback) {
    if (this._DEBUG) console.log(this.pageName + ' - verifyAccount() called');

    if (!this._pds) {
      this._pds = new PennyDropService(this.presenter);
    }
    this._pds.initiateVerification(this.id, function (successCallback, data) {
      var index = this._getAccountArrayIndex();
      this.presenter.updateModel('banking.accounts.bank_accounts[' + index + ']', data, successCallback.bind(null, this));
    }.bind(this, successCallback), failureCallback);
  };

  this.verifyBankStatus = function (successCallback, failureCallback) {
    if (this._DEBUG) console.log(this.pageName + ' - verifyBankStatus() called');

    var index = this._getAccountArrayIndex();
    var verifyBankAction = 'banking.accounts.bank_accounts[' + index + '].verify_bank_account';

    if (this.presenter.hasAction(verifyBankAction)) {
      this.presenter.performAction(verifyBankAction, null, this._onVerifyBankStatus.bind(this, successCallback, failureCallback), 6000);
    } else if (this.presenter.hasAction('banking.accounts.bank_accounts[' + index + '].bank_account_verification_result')) {
      this._onVerifyBankStatus.bind(this, successCallback, failureCallback);
    } else {
      failureCallback({ errors: [{ code: 404, key: 'no_action_error' }] }, 404);
    }
  };

  /*
    Polls bank account verification results and invokes callbacks from verifyBankStatus
  */
  this._onVerifyBankStatus = function (successCallback, failureCallback, data, response) {
    var index = this._getAccountArrayIndex();
    var bankResultAction = 'banking.accounts.bank_accounts[' + index + '].bank_account_verification_result';

    if (response === 200) {
      // Update bank account, bankResultAction relink will be there
      this.presenter.updateModel('banking.accounts.bank_accounts[' + index + ']', null, function (successCallback, failureCallback) {
        if (this.presenter.hasAction(bankResultAction)) {
          // Remove existing action callback
          if (this.bankResultCallbackId) {
            this.presenter.removeCallbackFromAction(bankResultAction, this._onPollBankResultCallback.bind(this));
          }

          this.bankResultCallbackId = this.presenter.addCallbackToAction(bankResultAction, this._onPollBankResultCallback.bind(this, successCallback, failureCallback));

          // Start Poll Action, add callback to action and check conditions in there
          // recall addCallback to action will have data, result
          this._timer = new Date();
          this.presenter.startPollAction(bankResultAction);
        }
      }.bind(this, successCallback, failureCallback));
    } else {
      failureCallback(data, response);
    }
  };

  this._onPollBankResultCallback = function (successCallback, failureCallback, data, response) {
    var index = this._getAccountArrayIndex();
    var bankResultAction = 'banking.accounts.bank_accounts[' + index + '].bank_account_verification_result';

    // Completed status means ABS is done,
    // results is to determine for member IE if they are fraudulent or deemed high risk
    if (response === 200 && data.status === 'completed') {
      this.presenter.stopPollAction(bankResultAction);

      // If results are pass or true, means statements are good
      if (data.result === 'pass' || data.result === true) {
        if (this._DEBUG) console.log(this.pageName + ' _onPollBankResultCallback() - Verification successful');
        successCallback(this);
      } else {
        if (this._DEBUG) console.log(this.pageName + ' _onPollBankResultCallback() - Verification failed with result:', data.result);
        failureCallback(data, response);
      }
    } else if (response !== 200 && (data.status !== 'processing' || data.status !== 'initiated')) {
      if (this._DEBUG) console.log(this.pageName + ' _onPollBankResultCallback() - Failed response:', response);
      this.presenter.stopPollAction(bankResultAction);
      failureCallback(data, response);
    }

    // Auto Timeout at 2 mins, temporary until backend fix
    if (Date.now() - this._timer.getTime() > 120000) {
      if (this._DEBUG) console.log(this.pageName + ' _onPollBankResultCallback() - Timeout after 2 minutes');
      this.presenter.stopPollAction(bankResultAction);
      failureCallback(data, response);
    }
  };

  /*
    Parameters:
      onSubmit
        When form is submitted (passes front-end validation)
  */
  this.confirmAccountVerification = function (node, submitCallback, successCallback, failureCallback) {
    if (this.getVerificationStatus() === 'pending') {
      if (!this._pds) {
        this._pds = new PennyDropService(this.presenter);
      }
      this._pds.confirmVerification(this.id, node, submitCallback, function (callback, data) {
        var index = this._getAccountArrayIndex();
        this.presenter.updateModel('banking.accounts.bank_accounts[' + index + ']', data, callback.bind(null, this));
      }.bind(this, successCallback), function (failureCallback, data, response) {
        if (data.penny_drop) {
          var index = this._getAccountArrayIndex();
          this.presenter.updateModel('banking.accounts.bank_accounts[' + index + ']', data, failureCallback.bind(null, this, data, response));
        } else {
          failureCallback(this, data, response);
        }
      }.bind(this, failureCallback));
    } else {
      failureCallback({ errors: [{ code: 404, key: 'no_action_error' }] }, 404);
    }
  };

  this.getVerificationStatus = function () {
    if (!this._pds) {
      this._pds = new PennyDropService(this.presenter);
    }
    return this._pds.getVerificationState(this.id);
  };

  // ps is Plaid Service
  this.linkAccount = function (successCallback, failureCallback, configObj) {
    var _this3 = this;

    if (this._DEBUG) console.log(this.pageName + ' - linkAccount() called');

    this._initiatePlaid(function (initiatePlaidResponse) {
      // success callback of initiatePlaid
      var ps = new PlaidService(_this3.presenter, configObj);
      ps.linkAccount(initiatePlaidResponse, function (plaidSuccessResponse) {
        // success callback of PS.linkAccount
        var index = _this3._getAccountArrayIndex();
        if (_this3.presenter.hasAction('banking.accounts.bank_accounts[' + index + '].link_bank_account')) {
          _this3.presenter.performAction('banking.accounts.bank_accounts[' + index + '].link_bank_account', ps.generateLinkAccountPayload(plaidSuccessResponse), function (data, response) {
            if (response === 200) {
              if (typeof successCallback === 'function') {
                _this3.presenter.updateModel('banking.accounts.bank_accounts[' + index + ']', data,
                // The second 'this' of bind refers to the current BankAccount instance itself. It is passed to the success callback method as it first argument.
                successCallback.bind(_this3, _this3));
              }
            } else {
              // Handle link_account soa errors here
              if (typeof failureCallback === 'function') {
                failureCallback(data, response);
              }
            }
          });
        } else {
          failureCallback({ errors: [{ code: 404, key: 'no_action_error' }] }, 404);
        }
      }, function (args) {
        // failure callback of PS.linkAccount
        var _errorCode = args[0] && args[0].error_code ? args[0].error_code : null;
        var _errorType = args[0] && args[0].error_type ? args[0].error_type : null;

        // this.errorHandler is defined in BankController
        _this3.errorHandler.invokeError(_errorCode, _errorType, failureCallback, true);
      });
    }, function (data, response) {
      // failure callback of initiatePlaid
      // Handle initiate_plaid soa errors here
      if (typeof failureCallback === 'function') {
        failureCallback(data, response);
      }
    });
  };

  this.relinkAccount = function (successCallback, failureCallback, configObj) {
    var index = this._getAccountArrayIndex();
    if (this.presenter.hasAction('banking.accounts.bank_accounts[' + index + '].get_plaid_update_token')) {
      this.presenter.performAction('banking.accounts.bank_accounts[' + index + '].get_plaid_update_token', null, function (successCallback, failureCallback, data, response) {
        if (response === 200) {
          var ps = new PlaidService(this.presenter, configObj);
          ps.linkAccount(data, function (successCallback, failureCallback, plaidSuccessResponse) {
            // Plaid success callback

            this.presenter.performAction('banking.accounts.bank_accounts[' + index + '].relink_bank_account', ps.generateUpdateAccountPayload(plaidSuccessResponse), function (successCallback, failureCallback, data2, response2) {
              if (response2 === 200) {
                // The second 'this' of bind refers to the current BankAccount instance itself. It is passed to the success callback method as it first argument.
                this.presenter.updateModel('banking.accounts.bank_accounts[' + index + ']', data2, successCallback.bind(this, this));
              } else {
                // Handle relink_bank_account soa errors here
                if (typeof failureCallback === 'function') {
                  failureCallback(data2, response2);
                }
              }
            }.bind(this, successCallback, failureCallback));
          }.bind(this, successCallback, failureCallback), function (failureCallback, plaidFailureResponse) {
            // Plaid failure callback
            var failurePayload = {
              error: plaidFailureResponse[1],
              public_token: plaidFailureResponse[0]
            };

            this.presenter.performAction('banking.accounts.bank_accounts[' + index + '].relink_bank_account', failurePayload, function (pfr, failureCallback, data, response) {
              var _errorCode = pfr[0] && pfr[0].error_code ? pfr[0].error_code : null;
              var _errorType = pfr[0] && pfr[0].error_type ? pfr[0].error_type : null;
              this.errorHandler.invokeError(_errorCode, _errorType, failureCallback, true);
            }.bind(this, plaidFailureResponse, failureCallback));
          }.bind(this, failureCallback));
        } else {
          // Handle get_plaid_update_token soa errors here
          this.errorHandler.invokeError(data.error_code, data.error_type, failureCallback);
        }
      }.bind(this, successCallback, failureCallback));
    } else {
      failureCallback({ errors: [{ code: 404, key: 'no_action_error' }] }, 404);
    }
  };

  this.getAccountState = function () {
    return accountModel.state;
  };

  this.isLinked = function () {
    return this.getAccountState() === 'linked';
  };

  this.isVerified = function () {
    return accountModel.access_verified;
  };

  this.getActionRequired = function () {
    return accountModel.action_required;
  };

  // TODO: should lookup name from Banks Table instead
  this.getBankName = function () {
    return accountModel.bank_name;
  };

  this.getFullAccountName = function () {
    if (!this.getBankName()) return null;
    return this.getBankName() + ' - ' + this.getAccountType();
  };

  this.getAccountHolderName = function () {
    return accountModel.holder_name;
  };

  this.getInstitutionId = function () {
    return accountModel.institution_number;
  };

  this.getTransitNumber = function () {
    return accountModel.masked_transit_number;
  };

  this.getAccountNumber = function () {
    return accountModel.masked_account_number;
  };

  this.getAccountType = function () {
    return accountModel.type === 'checking' ? 'Checking' : 'Savings';
  };

  this.getBalance = function (successCallback, failureCallback) {
    var _this4 = this;

    // return accountModel.current_balance;
    var index = this._getAccountArrayIndex();
    if (this.presenter.hasAction('banking.accounts.bank_accounts[' + index + '].get_balances')) {
      this.presenter.performAction('banking.accounts.bank_accounts[' + index + '].get_balances', null, function (data, response) {
        if (response === 200) {
          // The second 'this' of bind refers to the current BankAccount instance itself. It is passed to the success callback method as it first argument.
          _this4.presenter.updateModel('banking.accounts.bank_accounts[' + index + ']', data, successCallback.bind(_this4, _this4));
        } else {
          failureCallback(data, response);
        }
      });
    } else {
      failureCallback({ errors: [{ code: 404, key: 'no_action_error' }] }, 404);
    }
  };

  this.isPadSigned = function () {
    return accountModel.pad_accepted_at !== null;
  };

  this.signPadAgreement = function (successCallback, failureCallback) {
    var _this5 = this;

    var index = this._getAccountArrayIndex();
    if (this.presenter.hasAction('banking.accounts.bank_accounts[' + index + '].sign_pad_agreement')) {
      this.presenter.performAction('banking.accounts.bank_accounts[' + index + '].sign_pad_agreement', { accepted_pad: true }, function (data, response) {
        if (response === 200) {
          // The second 'this' of bind refers to the current BankAccount instance itself. It is passed to the success callback method as it first argument.
          _this5.presenter.updateModel('banking.accounts.bank_accounts[' + index + ']', data, successCallback.bind(_this5, _this5));
        } else {
          failureCallback(data, response);
        }
      });
    } else {
      failureCallback({ errors: [{ code: 404, key: 'no_action_error' }] }, 404);
    }
  };

  this.updateAccount = function (newInfo, successCallback, failureCallback) {
    var _this6 = this;

    var index = this._getAccountArrayIndex();
    if (this.presenter.hasAction('banking.accounts.bank_accounts[' + index + '].update_bank_account')) {
      this.presenter.performAction('banking.accounts.bank_accounts[' + index + '].update_bank_account', newInfo, function (data, response) {
        if (response === 200) {
          // The second 'this' of bind refers to the current BankAccount instance itself. It is passed to the success callback method as it first argument.
          _this6.presenter.updateModel('banking.accounts.bank_accounts[' + index + ']', data, successCallback.bind(_this6, _this6));
        } else {
          failureCallback(data, response);
        }
      });
    } else {
      failureCallback({ errors: [{ code: 404, key: 'no_action_error' }] }, 404);
    }
  };

  this.unlinkAccount = function (successCallback, failureCallback) {
    var _this7 = this;

    var index = this._getAccountArrayIndex();
    if (this.presenter.hasAction('banking.accounts.bank_accounts[' + index + '].unlink_bank_account')) {
      this.presenter.performAction('banking.accounts.bank_accounts[' + index + '].unlink_bank_account', null, function (data, response) {
        if (response === 200) {
          // The second 'this' of bind refers to the current BankAccount instance itself. It is passed to the success callback method as it first argument.
          _this7.presenter.updateModel('banking.accounts.bank_accounts[' + index + ']', data, successCallback.bind(_this7, _this7));
        } else {
          failureCallback(data, response);
        }
      });
    } else {
      failureCallback({ errors: [{ code: 404, key: 'no_action_error' }] }, 404);
    }
  };
}

BankAccount.productType = {
  card: 'card',
  crypto: 'crypto',
  loan: 'loan'
};